/* RESPONSIBLE TEAM: team-reporting */
import ChartDataResourceCompatible from 'embercom/lib/reporting/chart-data-resource-compatible';
import DataConfigBuilder from 'embercom/lib/reporting/custom/data-config-builder';
import ViewConfigBuilder from 'embercom/lib/reporting/custom/view-config-builder';
import HighchartsDataBuilder from 'embercom/lib/reporting/flexible/highcharts-data-builder';
import TableDataConfigBuilder from 'embercom/lib/reporting/custom/table-data-config-builder';
import { getOwner } from '@ember/application';
import { zip } from 'underscore';
import { isPresent } from '@ember/utils';
import { cached } from 'tracked-toolbox';
interface PercentageMetricValue {
  value: number | null;
  numeratorValue: number;
  denominatorValue: number;
}

type FlexibleResponsePercentageMetricValue = PercentageMetricValue & { key: string };
type FlexibleResponseMetricValue = FlexibleResponsePercentageMetricValue | number;

interface SignalFlexibleResponseViewBy {
  groups: Array<{
    values: SegmentByKey[];
    aggregations: Array<{
      name: string;
      values: number[];
      processedValues?: PercentageMetricValue[];
    }>;
    name: string;
    type: string;
  }>;
}

type ViewByKey = string | number;
type SegmentByKey = string | number;
export type MetricValue = number | PercentageMetricValue;
export type Series = Array<[ViewByKey, MetricValue | null]>;

interface SignalFlexibleResponseSegmentBy {
  groups: Array<{
    values: Array<SignalFlexibleResponseViewBy & { value: ViewByKey }>;
  }>;
}

export function flattenSegmentBy(
  segmentBy?: SignalFlexibleResponseSegmentBy,
): Array<[SegmentByKey, Series]> {
  if (!segmentBy) {
    return [];
  }
  let series = new Map<SegmentByKey, Series>();
  for (let viewBy of segmentBy.groups[0].values) {
    let dataForViewBy = flattenViewBy(viewBy);
    for (let [key, value] of dataForViewBy) {
      if (!series.has(key)) {
        series.set(key, []);
      }
      series.get(key)?.push([viewBy.value, value]);
    }
  }
  // Ensure we have a null value for every viewBy key if needed
  let requiredViewByKeys = segmentBy.groups[0].values.map(({ value }) => value);
  for (let [segmentByKey, seriesEntries] of series) {
    let knownSeriesEntries = Object.fromEntries(seriesEntries);
    let seriesWithNulls: Series = requiredViewByKeys.map((key) => [
      key,
      knownSeriesEntries[key] || null,
    ]);
    series.set(segmentByKey, seriesWithNulls);
  }
  return Array.from(series.entries());
}

function flattenViewBy(responseData: SignalFlexibleResponseViewBy): Series {
  let data = responseData.groups[0] || responseData;
  let aggregations = data.aggregations?.[0];
  if (aggregations === undefined) {
    return [];
  }
  let keys = data.values;
  let values = aggregations.processedValues || aggregations.values;
  return zip(keys, values) as Series;
}

function isPercentageMetricValue(
  value: FlexibleResponseMetricValue,
): value is FlexibleResponsePercentageMetricValue {
  return (
    typeof value === 'object' &&
    (isPresent(value.numeratorValue) || isPresent(value.denominatorValue))
  );
}

function buildMetricValue(value: FlexibleResponseMetricValue): MetricValue {
  if (isPercentageMetricValue(value)) {
    return {
      value: value.value,
      numeratorValue: value.numeratorValue,
      denominatorValue: value.denominatorValue,
    };
  }
  return value;
}

export default class ChartDataResource extends ChartDataResourceCompatible {
  constructor(owner: unknown, args: any) {
    let { renderableChart, onError } = args.named || args;

    let dataConfig = renderableChart.isTable
      ? new TableDataConfigBuilder(renderableChart).buildDataConfig()
      : new DataConfigBuilder(renderableChart).buildDataConfig();
    super(owner, { dataConfig, onError, renderableChart });

    this.viewConfig = new ViewConfigBuilder(renderableChart, owner).buildViewConfig();
  }

  viewConfig?: any;

  @cached
  get flatData() {
    return this.rawChartData.map(flattenViewBy);
  }

  @cached
  get flatCombinedData() {
    let lookups = this.flatData.map(Object.fromEntries);
    let allKeys = new Set(lookups.flatMap(Object.keys));
    let results: Record<string, MetricValue[]> = {};
    for (let key of allKeys) {
      let valuesForAllMetrics = lookups.map((lookup) => buildMetricValue(lookup[key]));
      results[key] = valuesForAllMetrics;
    }
    return results;
  }

  get kpiData() {
    return Object.values(this.flatCombinedData)[0];
  }

  get hasData() {
    if (!this.rawChartData) {
      return false;
    }

    return this.rawChartData.some((singleSeries) => {
      if (this.renderableChart.segmentBy) {
        let response = singleSeries as SignalFlexibleResponseSegmentBy;
        return response.groups.some((group) => {
          return group.values.some((value) => {
            return value.groups.some((innerGroup) => {
              return innerGroup.aggregations.some((aggregation) =>
                aggregation.values.some((value) => value !== 0),
              );
            });
          });
        });
      } else if (this.renderableChart.viewBy) {
        let response = singleSeries as SignalFlexibleResponseViewBy;
        return response.groups.some((group) => {
          return group.aggregations.some((aggregation) =>
            aggregation.values.some((value) => value !== 0),
          );
        });
      } else {
        // KPI query
        return singleSeries.aggregations[0].values.some((value: number) => value !== 0);
      }
    });
  }

  @cached
  get chartData() {
    if (!this.rawChartData) {
      return [];
    }

    if (this.renderableChart.stacked) {
      // stacked charts
      return new HighchartsDataBuilder(
        getOwner(this),
        this.dataConfig,
        this.viewConfig,
      ).forStackedChartResponse(this.rawChartData);
    }

    return this.rawChartData.map((dataResponse) => {
      if (!this.renderableChart.viewBy) {
        // simple count
        return {
          name: dataResponse.name,
          data: [dataResponse.aggregations[0].values[0]],
          requestName: dataResponse.name,
        };
      } else if (this.renderableChart.stacked === false) {
        // bar/line chart
        let group = dataResponse.groups[0];
        let aggregation = group.aggregations[0];

        return group.values.map((gv: any, index: number) => {
          let x = gv;
          let y = aggregation.values[index];
          let name = group?.names?.get(index);
          return [x, y, name];
        });
      }
    });
  }
}
